import { useEffect, useState, useRef, useContext } from 'react';
import { getAllComments, postComments } from '../../../services/discussionForum';
import { showError, utcToLocalDateTime, Spinner } from '../../../Utils/helper';
import { icons } from '../../../../constants/icons';
import { UserContext } from '../../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { fileUpload } from '../../../Utils/fileUpload';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { VoiceRecorder, VoiceRecorderMicButton, getFileExtFromMime } from '../../../VoiceRecorder';
import './Chat.css';

const VOICE_EXTENSIONS = ['.webm', '.m4a', '.aac', '.ogg'];
const isVoiceMessage = (filename) => filename && VOICE_EXTENSIONS.some((ext) => filename.endsWith(ext));

function Chat({ studentData }) {
    const { t } = useTranslation();
    const messageBox = useRef(null);
    const { user } = useContext(UserContext);

    const [uploadProgress, setUploadProgress] = useState({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });
    const [isUploading, setIsUploading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);

    const onProgress = (progress) => {
        setUploadProgress(progress);
    };
    const [data, setData] = useState([]);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const arrow = (
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="17.5px" height="17.5px" viewBox="0 0 535.5 535.5">
            <g id="send">
                <polygon points="0,497.25 535.5,267.75 0,38.25 0,216.75 382.5,267.75 0,318.75" />
            </g>
        </svg>
    );

    async function getComments() {
        setLoading(true);
        const resp = await getAllComments(studentData.id);
        if (resp.success) {
            setData(resp.data.reverse());
        } else {
            showError(t('alertMessage.wrong'));
        }
        setLoading(false);
    }

    useEffect(() => {
        getComments();
    }, []);

    async function sendMessage(e) {
        e.preventDefault();
        setMessage('');
        setLoading(true);
        if (message.length > 0) {
            let payload = {
                comment: message,
            };
            const resp = await postComments(studentData.id, payload);
            if (resp.success) {
                getComments();
            } else {
                showError(t('alertMessage.wrong'));
            }
        }
    }

    function handleFileChange(e) {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size / 1024 / 1024 < 30) {
            sendAttachment(file);
        } else {
            e.target.value = null;
            showError(t('alertMessage.fileNotMoreThan'));
        }
    }

    const getAttachmentUrl = (path) => {
        return process.env.REACT_APP_CONTENT_URL + path;
    };

    async function sendAttachment(file) {
        if (!file) return;
        setIsUploading(true);
        setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });

        try {
            const filePath = await fileUpload(file, 'storage/attachments', onProgress);

            let payload = {
                msg_type: 'attachment',
                comment: 'Image',
                attachment: '/' + filePath,
                file_ext: file.name.split('.').pop(),
            };

            const resp = await postComments(studentData.id, payload);
            if (resp.success) {
                getComments();
            } else {
                if (resp.status === 422) {
                    showError(t('alertMessage.fileNotMoreThan'));
                } else {
                    showError(t('alertMessage.wrong'));
                }
            }
        } catch (err) {
            console.error('Attachment upload failed:', err);
            showError(t('alertMessage.wrong'));
        } finally {
            setIsUploading(false);
            setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });
        }
    }

    async function sendVoiceRecording(audioBlob) {
        const ext = getFileExtFromMime(audioBlob.type);
        const timestamp = Date.now();
        const file = new File([audioBlob], `voice-${timestamp}.${ext}`, { type: audioBlob.type });

        setIsUploading(true);
        setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });

        try {
            const filePath = await fileUpload(file, 'storage/attachments', onProgress);

            const payload = {
                msg_type: 'attachment',
                comment: 'Voice message',
                attachment: '/' + filePath,
                file_ext: ext,
            };

            const resp = await postComments(studentData.id, payload);
            if (resp.success) {
                getComments();
            } else {
                showError(t('alertMessage.wrong'));
            }
        } catch (err) {
            console.error('Voice upload failed:', err);
            showError(t('alertMessage.wrong'));
        } finally {
            setIsUploading(false);
            setIsRecording(false);
            setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });
        }
    }

    const AlwaysScrollToBottom = () => {
        const elementRef = useRef();
        useEffect(() => {
            elementRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            window.scrollTo(0, 0);
        });
        return <div ref={elementRef} />;
    };

    return (
        <section id="chat-container" className="message_section">
            <div className="container">
                <div className="messages">
                    <div className="msg_header">
                        <div className="msg_profile">
                            <div>
                                {studentData?.first_name[0]}
                                {studentData?.last_name[0]}
                            </div>
                        </div>
                        <div className="msg_profile_details">
                            <div className="msg_name">
                                {studentData?.first_name} {studentData?.last_name}
                            </div>
                        </div>
                    </div>

                    <div id="chat-window" className="msg_win">
                        {loading ? (
                            <Box
                                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {data?.length === 0 ? (
                                    <div className="d-flex justify-content-center align-items-center empty-chat">
                                        <h2 className="text-center">{t('common.startConversation')}</h2>
                                    </div>
                                ) : (
                                    <>
                                        {data?.map((_comment) => (
                                            <div key={_comment?.id}>
                                                {_comment?.user_id == studentData.id ? (
                                                    <div className="msg_row msg_receiver">
                                                        {console.log(_comment)}
                                                        {_comment.attachment !== '' && _comment.attachment !== null ? (
                                                            <div className="msg_file">
                                                                {isVoiceMessage(
                                                                    _comment?.attachment?.attachment_file
                                                                ) ? (
                                                                    <div className="msg_voice">
                                                                        <audio
                                                                            controls
                                                                            src={getAttachmentUrl(
                                                                                _comment?.attachment?.attachment_path
                                                                            )}
                                                                            style={{
                                                                                height: '36px',
                                                                                maxWidth: '250px',
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <a
                                                                            href={getAttachmentUrl(
                                                                                _comment?.attachment?.attachment_path
                                                                            )}
                                                                            className="download_button"
                                                                            download
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            <img
                                                                                src={icons.icon_download_black}
                                                                                alt="download"
                                                                            />
                                                                        </a>
                                                                        {_comment?.attachment?.attachment_file}
                                                                    </>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="msg_text">{_comment?.comment}</div>
                                                        )}
                                                        <div className="msg_date">
                                                            {utcToLocalDateTime(_comment?.created_at)}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="msg_row msg_sender">
                                                        {_comment.attachment !== '' && _comment.attachment !== null ? (
                                                            <div className="msg_file">
                                                                {isVoiceMessage(
                                                                    _comment?.attachment?.attachment_file
                                                                ) ? (
                                                                    <div className="msg_voice">
                                                                        <audio
                                                                            controls
                                                                            src={getAttachmentUrl(
                                                                                _comment?.attachment?.attachment_path
                                                                            )}
                                                                            style={{
                                                                                height: '36px',
                                                                                maxWidth: '250px',
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <a
                                                                            href={getAttachmentUrl(
                                                                                _comment?.attachment?.attachment_path
                                                                            )}
                                                                            className="download_button"
                                                                            download
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            <img
                                                                                src={icons.icon_download}
                                                                                alt="download"
                                                                            />
                                                                        </a>
                                                                        {_comment?.attachment?.attachment_file}
                                                                    </>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="msg_text">{_comment?.comment}</div>
                                                        )}
                                                        <div className="msg_date">
                                                            {_comment?.human_readable_created_at}
                                                        </div>
                                                    </div>
                                                )}
                                                <AlwaysScrollToBottom />
                                            </div>
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    <div className="msg_footer">
                        {isRecording ? (
                            <VoiceRecorder
                                onSend={(blob) => sendVoiceRecording(blob)}
                                onCancel={() => setIsRecording(false)}
                                isUploading={isUploading}
                            />
                        ) : (
                            <div className="msg_input">
                                <form onSubmit={sendMessage} className="grey_input w-100 mt-0">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t('common.typeMessage')}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                    />
                                    <div className="attachment_button">
                                        <input type="file" id="attachment" onChange={handleFileChange} />
                                        <label className="form-label" htmlFor="attachment">
                                            <img src={icons.icon_attachment} alt="" />
                                        </label>
                                    </div>

                                    <VoiceRecorderMicButton className="ml-2" onClick={() => setIsRecording(true)} />

                                    <span className="ml-2 svg_icon cursor_pointer" onClick={sendMessage}>
                                        {arrow}
                                    </span>
                                </form>
                            </div>
                        )}

                        {isUploading && uploadProgress.percent > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: '8px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        fontSize: '12px',
                                        color: '#6b7280',
                                        marginBottom: '4px',
                                    }}
                                >
                                    <span>
                                        {uploadProgress.uploadedMB.toFixed(1)}MB / {uploadProgress.totalMB.toFixed(1)}MB
                                        @ {uploadProgress.speedMbps.toFixed(1)} MB/s
                                    </span>
                                    <span>{uploadProgress.percent}%</span>
                                </div>
                                <div
                                    style={{
                                        width: '100%',
                                        backgroundColor: '#e5e7eb',
                                        borderRadius: '9999px',
                                        height: '8px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: `${uploadProgress.percent}%`,
                                            backgroundColor: '#3b82f6',
                                            height: '8px',
                                            borderRadius: '9999px',
                                            transition: 'width 0.3s ease',
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Chat;
